import { Check, ChevronDown, Ticket, CalendarClock } from "lucide-react";
import type { ItemSegment, ItineraryDay } from "@/lib/types";
import { ItemRow } from "../itinerary/SegmentRow";
import { ITEM_ICON } from "../itinerary/itinerary-utils";
import {
  dayHeading,
  dayOrdinal,
  daySummary,
  ongoingRowLabel,
  timelineRows,
} from "./itineraryDay-utils";

/** An event found in the trip description that nobody has confirmed yet. */
export interface PendingEvent {
  title: string;
}

interface ItineraryDayCardProps {
  day: ItineraryDay;
  /** Position in the trip, 0-based — drives the "Day 4" chip. */
  index: number;
  expanded: boolean;
  onToggle: (date: string) => void;
  onEditItem: (seg: ItemSegment) => void;
  onDeleteItem: (seg: ItemSegment) => void;
  deletingId?: string | null;
  /** A proposed event on this day, shown as an unconfirmed row with a Confirm. */
  pendingEvent?: PendingEvent | null;
  onConfirmPending?: () => void;
  confirmingPending?: boolean;
  /** The printed document: always open, no controls. */
  printMode?: boolean;
}

/**
 * One day of the trip, in a card you can shut.
 *
 * Collapsed, the header alone must answer "what happens this day?" — that is
 * `daySummary`. Expanded, the day reads in time order with the things that
 * merely CONTINUE (a hotel you are still in, a cruise you are still on) sitting
 * quietly above the things that actually HAPPEN.
 */
export default function ItineraryDayCard({
  day,
  index,
  expanded,
  onToggle,
  onEditItem,
  onDeleteItem,
  deletingId,
  pendingEvent = null,
  onConfirmPending,
  confirmingPending = false,
  printMode = false,
}: ItineraryDayCardProps) {
  const heading = dayHeading(day.date);
  const daySum = daySummary(day);
  // A proposed event is something on this day, so the day is not "Nothing planned".
  const summary = pendingEvent
    ? {
        ...daySum,
        empty: false,
        text: [pendingEvent.title + " (unconfirmed)", ...daySum.parts].join(" · "),
      }
    : daySum;
  const rows = timelineRows(day);
  const open = printMode || expanded;
  const bodyId = "day-body-" + day.date;

  const headerInner = (
    <>
      <span
        className={
          "flex h-9 w-9 shrink-0 flex-col items-center justify-center rounded-lg text-[10px] font-bold leading-none " +
          (day.empty && !pendingEvent
            ? "bg-muted text-muted-foreground"
            : "bg-[hsl(var(--primary))]/[0.1] text-[hsl(var(--accent-foreground))]")
        }
        aria-hidden="true"
      >
        <span className="opacity-70">DAY</span>
        <span className="mt-0.5 text-xs tabular-nums">{index + 1}</span>
      </span>

      <span className="min-w-0 flex-1">
        <span className="flex flex-wrap items-baseline gap-x-2">
          <span className="text-sm font-semibold text-foreground">
            <span className="sm:hidden">{heading.weekdayShort}</span>
            <span className="hidden sm:inline">{heading.weekday}</span>
          </span>
          <span className="text-xs tabular-nums text-muted-foreground">
            {heading.date}
          </span>
        </span>
        <span
          className={
            "mt-0.5 block text-xs leading-snug " +
            (summary.empty
              ? "italic text-muted-foreground/70"
              : "text-muted-foreground")
          }
        >
          {summary.text}
        </span>
      </span>
    </>
  );

  return (
    <article
      className={
        "itinerary-day overflow-hidden rounded-xl border bg-card shadow-sm " +
        (day.empty ? "border-dashed border-border" : "border-border")
      }
      aria-label={dayOrdinal(index) + " — " + heading.full}
    >
      {printMode ? (
        <div className="flex items-start gap-3 px-3 py-3 sm:px-4">
          {headerInner}
        </div>
      ) : (
        <button
          type="button"
          onClick={() => onToggle(day.date)}
          aria-expanded={expanded}
          aria-controls={bodyId}
          className="itinerary-day-header flex min-h-[56px] w-full items-start gap-3 px-3 py-3 text-left transition-colors hover:bg-muted/40 motion-reduce:transition-none sm:px-4"
        >
          {headerInner}
          <ChevronDown
            className={
              "itinerary-day-chevron mt-1 h-4 w-4 shrink-0 text-muted-foreground transition-transform motion-reduce:transition-none " +
              (expanded ? "rotate-180" : "")
            }
            aria-hidden="true"
          />
        </button>
      )}

      {open && (
        <div
          id={bodyId}
          className="space-y-2 border-t border-border px-3 py-3 sm:px-4"
        >
          {rows.map((row) => {
            if (row.kind === "ongoing") {
              const Icon = ITEM_ICON[row.span.item_type] ?? Ticket;
              return (
                <div
                  key={"ongoing-" + row.span.item_id}
                  className="flex items-center gap-2.5 rounded-lg border border-dashed border-border/70 bg-muted/30 px-3 py-2 text-xs text-muted-foreground"
                >
                  <Icon className="h-3.5 w-3.5 shrink-0" aria-hidden="true" />
                  <span className="min-w-0">
                    {ongoingRowLabel(row.span)}
                    {row.span.city && (
                      <span className="opacity-70"> · {row.span.city}</span>
                    )}
                  </span>
                </div>
              );
            }
            const seg = row.segment;
            // Flights are the timeline's backbone, drawn between sections.
            if (seg.kind === "flight") return null;
            return (
              <ItemRow
                key={"i-" + seg.id}
                seg={seg}
                onEdit={onEditItem}
                onDelete={onDeleteItem}
                deleting={deletingId === seg.id}
                readOnly={printMode}
              />
            );
          })}

          {pendingEvent && !printMode && (
            <div className="flex flex-wrap items-center gap-2 rounded-lg border border-dashed border-amber-300 bg-amber-50/50 px-3 py-2 text-sm dark:border-amber-500/40 dark:bg-amber-500/10">
              <CalendarClock className="h-4 w-4 shrink-0 text-amber-700 dark:text-amber-400" aria-hidden="true" />
              <span className="min-w-0 flex-1">
                <span className="font-medium text-foreground">{pendingEvent.title}</span>{" "}
                <span className="text-xs text-muted-foreground">
                  · unconfirmed, from your trip description
                </span>
              </span>
              <button
                type="button"
                onClick={onConfirmPending}
                disabled={confirmingPending}
                className="inline-flex min-h-[32px] items-center gap-1 rounded-md bg-[hsl(var(--primary))] px-2.5 py-1 text-xs font-medium text-primary-foreground transition-opacity hover:opacity-90 disabled:opacity-60"
              >
                <Check className="h-3.5 w-3.5" aria-hidden="true" />
                Confirm
              </button>
            </div>
          )}

          {day.empty && !pendingEvent && (
            <p className="px-1 py-2 text-xs leading-snug text-muted-foreground">
              A free day. Add a meal, a walk, or leave it open on purpose.
            </p>
          )}

        </div>
      )}
    </article>
  );
}
